import logger from 'kolibri-logging';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import matches from 'lodash/matches';
import isEqual from 'lodash/isEqual';
import qs from 'qs';
import { ref, shallowRef, computed } from 'vue';
import { toValue } from '@vueuse/core';
import urls from 'kolibri/urls';
import useFetch from 'kolibri/composables/useFetch';
import sanitizeError from './utils/sanitizeError';

export const logging = logger.getLogger(__filename);

// Serialize query parameters into a string that is stable regardless of key insertion order,
// so that two equivalent parameter objects produce the same in-flight request key.
const stableSerialize = params => qs.stringify(params, { sort: (a, b) => a.localeCompare(b) });

/** Class representing a single API resource object */
export class Model {
  /**
   * Create a model instance.
   * @param {object} data - data to insert into the model at creation time - should include at
   * least an id for fetching, or data an no id if the intention is to save a new model.
   * @param {object} getParams - an object of parameters to be parsed into GET parameters on the
   * fetch.
   * @param {Resource} resource - object of the Resource class, specifies the urls and fetching
   * behaviour for the model.
   * @param {string} url - URL used for model-level fetch/save/delete requests
   */
  constructor(data, getParams = {}, resource, url) {
    this.resource = resource;
    if (!this.resource) {
      throw new TypeError('resource must be defined');
    }

    if (!data) {
      throw new TypeError('data must be defined');
    }

    if (typeof data !== 'object') {
      throw new TypeError('data must be an object');
    }

    if (Object.keys(data).length === 0) {
      throw new TypeError('data must be instantiated with some data');
    }

    // Assign any data to the attributes property of the Model.
    this.attributes = {};
    this.set(data);

    this.getParams = getParams;

    this.url = url;

    this.synced = false;
    // Set this property to track whether this model exists on the server or not
    // Assume it does until we learn otherwise
    this.new = true;

    // Keep track of any unresolved promises that have been generated by async methods of the Model
    this.promises = [];
  }

  /**
   * Method to fetch data from the server for this particular model.
   * @param {boolean} [force=false] - Fetch regardless of sync state
   * @returns {Promise} - Promise is resolved with Model attributes when the XHR successfully
   * returns, otherwise reject is called with the response object.
   */
  fetch(force = false) {
    const promise = new Promise((resolve, reject) => {
      Promise.all(this.promises).then(
        () => {
          if (!force && this.synced) {
            resolve(this.data);
          } else {
            this.synced = false;
            // Do a fetch on the URL.
            this.resource.client({ url: this.url, params: this.getParams }).then(
              response => {
                // Set the retrieved Object onto the Model instance.
                this.set(response.data);
                // Flag that the Model has been fetched.
                this.synced = true;
                // Flag that the model exists on the server.
                this.new = false;
                // Resolve the promise with the attributes of the Model.
                resolve(this.data);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
              response => {
                this.resource.logError(response);
                reject(response);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
            );
          }
        },
        reason => {
          reject(reason);
        },
      );
    });
    this.promises.push(promise);
    return promise;
  }

  /**
   * Method to save data to the server for this particular model.
   * @param {object} attrs - an object of attributes to be saved on the model.
   * @param {boolean} exists - a Boolean flag to override the default new behaviour
   * @returns {Promise} - Promise is resolved with Model attributes when the XHR successfully
   * returns, otherwise reject is called with the response object.
   */
  save(attrs, exists = false) {
    const promise = new Promise((resolve, reject) => {
      Promise.all(this.promises).then(
        () => {
          let payload = {};
          if (this.synced) {
            // Model is synced with the server, so we can do dirty checking.
            Object.keys(attrs).forEach(key => {
              if (!isEqual(attrs[key], this.attributes[key])) {
                payload[key] = attrs[key];
              }
            });
          } else {
            payload = {
              ...this.attributes,
              ...attrs,
            };
          }
          if (!Object.keys(payload).length) {
            // Nothing to save, so just resolve the promise now.
            resolve(this.data);
          } else {
            this.synced = false;
            let url;
            let clientObj;
            if (!this.new || exists) {
              // If this Model is not new, then can do a PATCH against the Model
              url = this.url;
              clientObj = { url: url, method: 'patch', data: payload, params: this.getParams };
            } else {
              // Otherwise, must POST to the Collection endpoint to create the Model
              url = this.resource.collectionUrl();
              clientObj = { url: url, method: 'post', data: payload, params: this.getParams };
            }
            // Do a save on the URL.
            this.resource.client(clientObj).then(
              response => {
                const oldId = this.id;
                // Set the retrieved Object onto the Model instance.
                this.set(response.data);
                // if the model did not used to have an id and now does, add it to the cache.
                if (!oldId && this.id) {
                  this.resource.addModel(this, this.getParams);
                }
                // Flag that the Model has been fetched.
                this.synced = true;
                // Flag that the model exists on the server.
                this.new = false;
                // Resolve the promise with the Model.
                resolve(this.data);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
              response => {
                this.resource.logError(response);
                reject(response);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
            );
          }
        },
        reason => {
          reject(reason);
        },
      );
    });
    this.promises.push(promise);
    return promise;
  }

  /**
   * Method to delete the model on the server.
   * @returns {Promise} - Promise is resolved with target model's id
   * returns, otherwise reject is called with the response object.
   */
  delete() {
    const promise = new Promise((resolve, reject) => {
      Promise.all(this.promises).then(
        () => {
          if (!this.id) {
            // Nothing to delete, so just resolve the promise now.
            reject('Can not delete model that we do not have an id for');
          } else {
            // Otherwise, DELETE the Model
            const clientObj = { url: this.url, method: 'delete', params: this.getParams };
            this.resource.client(clientObj).then(
              () => {
                // delete this instance
                this.resource.removeModel(this);
                // Set a flag so that any collection containing this can ignore this model
                this.deleted = true;
                // Any collection containing this model is now probably out of date,
                // set synced to false to ensure that they update their data on fetch
                this.synced = false;
                this.new = true;
                // Resolve the promise with the id.
                // Vuex will use this id to delete the model in its state.
                resolve(this.id);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
              response => {
                this.resource.logError(response);
                reject(response);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
            );
          }
        },
        reason => {
          reject(reason);
        },
      );
    });
    this.promises.push(promise);
    return promise;
  }

  get url() {
    return this._url ? this._url : this.resource.modelUrl(this.id);
  }

  set url(url) {
    this._url = url;
  }

  get id() {
    return this.attributes[this.resource.idKey];
  }

  get data() {
    return cloneDeep(this.attributes);
  }

  set(attributes) {
    // force IDs to always be strings - this should be changed on the server-side too
    if (attributes && this.resource.idKey in attributes) {
      if (attributes[this.resource.idKey]) {
        // don't stringigy null or undefined.
        attributes[this.resource.idKey] = String(attributes[this.resource.idKey]);
      }
    }
    Object.assign(this.attributes, cloneDeep(attributes));
  }
}

/**
 * Class representing a 'view' of a single API resource.
 * Contains different Model objects, depending on the parameters passed to its fetch method.
 */
export class Collection {
  /**
   * Create a Collection instance.
   * @param {object} getParams - Default parameters to use when fetching data from the server.
   * @param {object[] | Model[]} data - Data to prepopulate the collection with,
   * useful if wanting to save multiple models.
   * @param {Resource} resource - object of the Resource class, specifies the urls and fetching
   * behaviour for the collection.
   * @param {Function} url - a url function for this collection if undefind default to list url
   */
  constructor(getParams = {}, data = [], resource, url) {
    this.resource = resource;
    this.getParams = getParams;
    if (!this.resource) {
      throw new TypeError('resource must be defined');
    }
    this.models = [];
    this._model_map = {};
    this.url = url;
    this.synced = false;
    this.new = true;
    this.set(data);
    // Keep track of any unresolved promises that have been generated by async methods of the Model
    this.promises = [];
  }

  /**
   * Method to fetch data from the server for this collection.
   * @param {boolean} force - fetch whether or not it's been synced already.
   * @returns {Promise} - Promise is resolved with Array of Model attributes when the XHR
   * successfully returns, otherwise reject is called with the response object.
   */
  fetch(force = false) {
    const promise = new Promise((resolve, reject) => {
      Promise.all(this.promises).then(
        () => {
          if (!force && this.synced) {
            resolve(this.data);
          } else {
            this.synced = false;
            this.resource.client({ url: this.url, params: this.getParams }).then(
              response => {
                // Set response object - an Array - on the Collection to record the data.
                // First check that the response *is* an Array
                if (Array.isArray(response.data)) {
                  this.clearCache();
                  this.set(response.data);
                  // Mark that the fetch has completed.
                  this.synced = true;
                  // Flag that the collection exists on the server.
                  this.new = false;
                } else if (typeof (response.data || {}).results !== 'undefined') {
                  // If it's not, there are two possibilities - something is awry,
                  // or we have received data with additional metadata!
                  this.clearCache();
                  // Collections with additional metadata have 'results' as their results
                  // object so interpret this as such.
                  this.set(response.data.results);
                  this.metadata = {};
                  Object.keys(response.data).forEach(key => {
                    if (key !== 'results') {
                      this.metadata[key] = response.data[key];
                    }
                  });
                  // Mark that the fetch has completed.
                  this.synced = true;
                  // Flag that the collection exists on the server.
                  this.new = false;
                } else {
                  // It's all gone a bit Pete Tong.
                  this.resource.logError(response);
                  reject(response);
                }
                resolve(this.data);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
              response => {
                this.resource.logError(response);
                reject(response);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
            );
          }
        },
        reason => {
          reject(reason);
        },
      );
    });
    this.promises.push(promise);
    return promise;
  }

  /**
   * Method to save data to the server for this particular collection.
   * Can only currently be used to save new models to the server, not do bulk updates.
   * @param {Array} [data] - Array of model payloads to POST; defaults to the collection's own data
   * @returns {Promise} - Promise is resolved with list of collection attributes when the XHR
   * successfully returns, otherwise reject is called with the response object.
   */
  save(data = []) {
    const promise = new Promise((resolve, reject) => {
      Promise.all(this.promises).then(
        () => {
          if (!data.length && !this.new) {
            // Collection is not new so constituent models must be synced, so already saved.
            reject('Cannot update collections, only create them');
            // Clean up the reference to this promise
            this.promises.splice(this.promises.indexOf(promise), 1);
            return;
          }
          this.synced = false;
          const url = this.resource.collectionUrl();
          const payload = data.length ? data : this.data;
          const clientObj = { url: url, data: payload, method: 'post' };
          // Do a save on the URL.
          this.resource.client(clientObj).then(
            response => {
              if (Array.isArray(response.data)) {
                this.clearCache();
                this.set(response.data);
                // Mark that the fetch has completed.
                this.synced = true;
                // Flag that the collection exists on the server.
                this.new = false;
              } else {
                // It's all gone a bit Pete Tong.
                logging.debug('Data appears to be malformed', response.data);
                reject(response);
              }
              // Resolve the promise with the Collection.
              resolve(this.data);
              // Clean up the reference to this promise
              this.promises.splice(this.promises.indexOf(promise), 1);
            },
            response => {
              this.resource.logError(response);
              reject(response);
              // Clean up the reference to this promise
              this.promises.splice(this.promises.indexOf(promise), 1);
            },
          );
        },
        reason => {
          reject(reason);
        },
      );
    });
    this.promises.push(promise);
    return promise;
  }

  /**
   * Method to delete a collection.
   * @returns {Promise} - Promise is resolved with list of collection ids
   * returns, otherwise reject is called with the response object.
   */
  delete() {
    const promise = new Promise((resolve, reject) => {
      Promise.all(this.promises).then(
        () => {
          if (!Object.keys(this.getParams).length) {
            // Cannot do a DELETE unless we are filtering by something,
            // to prevent dangerous bulk deletes
            reject('Can not delete unfiltered collection (collection without any GET params');
          } else {
            // Otherwise, DELETE the Collection
            const clientObj = {
              url: this.resource.collectionUrl(),
              method: 'delete',
              params: this.getParams,
            };
            this.resource.client(clientObj).then(
              () => {
                // delete this instance
                this.resource.removeCollection(this);
                // delete and remove each model
                this.models.forEach(model => {
                  model.deleted = true;
                  this.resource.removeModel(model);
                });
                // Vuex will use this id to delete the model in its state.
                resolve(this.models.map(model => model.id));
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
              response => {
                this.resource.logError(response);
                reject(response);
                // Clean up the reference to this promise
                this.promises.splice(this.promises.indexOf(promise), 1);
              },
            );
          }
        },
        reason => {
          reject(reason);
        },
      );
    });
    this.promises.push(promise);
    return promise;
  }

  get url() {
    return this._url ? this._url : this.resource.collectionUrl();
  }

  set url(url) {
    this._url = url;
  }

  /**
   * Clear this Collection's cache of models.
   */
  clearCache() {
    // Reset current models.
    this.models = [];
    this._model_map = {};
  }

  /**
   * Make a model a member of the collection - record in the models Array, and in the mapping
   * from id to model. Will automatically instantiate Models for data passed in as objects, and
   * deduplicate within the collection.
   * @param {(object | Model | object[] | Model[])} models - Either an Array or single instance of
   * an object or Model.
   */
  set(models) {
    let modelsToSet;
    if (!Array.isArray(models)) {
      modelsToSet = [models];
    } else {
      modelsToSet = models;
    }
    // Check if this collection is using dynamic field assignment, as we should cache these in a
    // separate namespace.
    const getParams = this.getParams.fields ? { fields: this.getParams.fields } : {};
    modelsToSet.forEach(model => {
      // Note: this method ensures instantiation deduplication of models within the collection
      // and across collections.

      const setModel = this.resource.addModel(model, getParams);
      let cacheKey;
      if (setModel.id) {
        cacheKey = setModel.id;
      } else {
        cacheKey = this.resource.__cacheKey(setModel.attributes);
      }
      if (!this._model_map[cacheKey]) {
        this._model_map[cacheKey] = setModel;
        this.models.push(setModel);
      }
    });
  }

  get data() {
    const data = this.models.filter(model => !model.deleted).map(model => model.data);
    // Return the data from the models, not the models themselves.
    if (!this.metadata) {
      // If no additional metadata just return the results directly.
      return data;
    }
    // Otherwise resolve the data in the form it was received originally
    return {
      results: data,
      ...cloneDeep(this.metadata),
    };
  }

  get synced() {
    // We only say the Collection is synced if it, itself, is synced, and all its
    // constituent models are also.
    return this.models.reduce((synced, model) => synced && model.synced, this._synced);
  }

  /**
   * Set this Collection as synced or not, for true, will also set all models cached in it
   * as synced.
   * @param {boolean} value - Is this Collection synced or not?
   */
  set synced(value) {
    this._synced = value;
    if (value) {
      this.models.forEach(model => {
        model.synced = true;
      });
    }
  }

  get new() {
    // We only say the Collection is new if it, itself, is not new, and all its
    // constituent models are also not new.
    return this.models.reduce((isNew, model) => isNew && model.new, this._new);
  }

  /**
   * Set this Collection as new or not, for false, will also set all models cached in it
   * as not new.
   * @param {boolean} value - Is this Collection new or not?
   */
  set new(value) {
    this._new = value;
    if (!value) {
      this.models.forEach(model => {
        model.new = false;
      });
    }
  }
}

/**
 * Class representing a single API resource.
 * Contains references to all Models that have been fetched from the server.
 * Can also be subclassed in order to create custom behaviour for particular API resources.
 */
export class Resource {
  /**
   * Create a resource with a Django REST API name corresponding to the name parameter.
   * @param {object} options - Configuration including `name`, `idKey`, `namespace` and any
   * additional properties to attach to the resource instance
   * @param {string} options.name - DRF router name for the resource endpoint
   * @param {string} [options.idKey] - Attribute used as the primary key (defaults to `id`)
   * @param {string} [options.namespace] - URL namespace prefix (defaults to `core`)
   */
  constructor({ name, idKey = 'id', namespace = 'core', ...options } = {}) {
    if (!name) {
      throw ReferenceError('Resource must be instantiated with a name property');
    }
    this.name = `kolibri:${namespace}:${name}`;
    this.idKey = idKey;
    const optionsDefinitions = Object.getOwnPropertyDescriptors(options);
    Object.keys(optionsDefinitions).forEach(key => {
      Object.defineProperty(this, key, optionsDefinitions[key]);
    });
    this.clearCache();
    // Currently pending GET requests, keyed by resolved URL and query params. This is request
    // coalescing, not a cache - an entry is removed as soon as its request settles.
    this._inFlight = new Map();
  }

  __cacheKey(...params) {
    const allParams = Object.assign({}, ...params);
    // Sort keys in order, then assign those keys to an empty object in that order.
    // Then stringify to create a cache key.
    return JSON.stringify(
      Object.assign(
        {},
        ...Object.keys(allParams)
          .sort()
          .map(paramKey => ({
            [paramKey]: paramKey === this.idKey ? String(allParams[paramKey]) : allParams[paramKey],
          })),
      ),
    );
  }

  __cacheName(endpointName) {
    return endpointName ? `endpoint-${endpointName}` : 'default';
  }

  __getCache(type, endpointName) {
    const cacheName = this.__cacheName(endpointName);
    this[type][cacheName] = this[type][cacheName] || {};
    return this[type][cacheName];
  }

  __collectionCache(endpointName) {
    return this.__getCache('collections', endpointName);
  }

  __modelCache(endpointName) {
    return this.__getCache('models', endpointName);
  }

  /**
   * Retrieve a cached Collection for the given params, creating one if absent.
   * @param {object} [getParams] - default parameters to use for Collection fetching.
   * @param {string} [endpointName] - Named detail/list endpoint to fetch against
   * @param {string} [detailId] - Parent detail id for nested endpoints
   * @returns {Collection} - Returns an instantiated Collection object.
   */
  getCollection(getParams = {}, endpointName, detailId) {
    const cache = this.__collectionCache(endpointName);
    const key = this.__cacheKey(getParams, { detailId });
    if (!cache[key]) {
      cache[key] = this.createCollection(getParams, [], endpointName, detailId);
    }
    return cache[key];
  }

  /**
   * Optionally pass in data and instantiate a collection for saving that data or fetching
   * data from the resource.
   * @param {object} [getParams] - default parameters to use for Collection fetching.
   * @param {object[]} [data] - Data to instantiate the Collection - see Model constructor for
   * details of data.
   * @param {string} [endpointName] - Named detail/list endpoint used for URL resolution
   * @param {string} [detailId] - Parent detail id for nested endpoints
   * @returns {Collection} - Returns an instantiated Collection object.
   */
  createCollection(getParams = {}, data = [], endpointName, detailId) {
    let url;
    if (endpointName && detailId) {
      url = this.getUrlFunction(endpointName)(detailId);
    } else if (endpointName) {
      url = this.getUrlFunction(endpointName)();
    }
    const cache = this.__collectionCache(endpointName);
    const key = this.__cacheKey(getParams, { detailId });
    const collection = new Collection(getParams, data, this, url);
    cache[key] = collection;
    return collection;
  }

  /**
   * Get a model by id
   * @param {string} id - The primary key of the Model instance.
   * @param {object} [getParams] - Query parameters to associate with the cached model
   * @param {string} [endpointName] - Named detail endpoint used for URL resolution
   * @returns {Model} - Returns a Model instance.
   */
  getModel(id, getParams = {}, endpointName) {
    const cache = this.__modelCache(endpointName);
    const cacheKey = this.__cacheKey({ [this.idKey]: id }, getParams);
    if (!cache[cacheKey]) {
      this.createModel({ [this.idKey]: id }, getParams, endpointName);
    }
    return cache[cacheKey];
  }

  /**
   * Find a model by its attributes - will return first model found that matches
   * @param {object} attrs - Hash of attributes to search by
   * @param {string} endpointName - name of endpoint to search model cache
   * @returns {Model} First matching Model
   */
  findModel(attrs, endpointName) {
    const cache = this.__modelCache(endpointName);
    return find(cache, model => matches(attrs)(model.attributes));
  }

  /**
   * Add a model to the resource for deduplication, dirty checking, and tracking purposes.
   * @param {object} data - The data for the model to add.
   * @param {object} [getParams] - Query parameters associated with the model
   * @param {string} [endpointName] - Named detail endpoint used for URL resolution
   * @returns {Model} - Returns the instantiated Model.
   */
  createModel(data, getParams = {}, endpointName) {
    let url;
    if (endpointName) {
      const detailId = data[this.idKey];
      url = this.getUrlFunction(endpointName)(detailId);
    }
    const model = new Model(data, getParams, this, url);
    return this.addModel(model, getParams, endpointName);
  }

  /**
   * Add a model to the resource for deduplication, dirty checking, and tracking purposes.
   * @param {object | Model} model - Either the data for the model to add, or the Model itself.
   * @param {object} [getParams] - Query parameters associated with the model
   * @param {string} [endpointName] - Named detail endpoint used for URL resolution
   * @returns {Model} - Returns the instantiated Model.
   */
  addModel(model, getParams = {}, endpointName) {
    if (!(model instanceof Model)) {
      return this.createModel(model, getParams, endpointName);
    }
    // Add to the model cache using the default key if id is defined.
    const cache = this.__modelCache(endpointName);
    let cacheKey;
    if (model.id) {
      cacheKey = this.__cacheKey({ [this.idKey]: model.id }, model.getParams);
      if (!cache[cacheKey]) {
        cache[cacheKey] = model;
      } else {
        cache[cacheKey].set(model.attributes);
      }
    } else {
      // Otherwise use a hash of the models attributes to create a temporary cache key
      cacheKey = this.__cacheKey(model.attributes);
      cache[cacheKey] = model;
      // invalidate collection cache because this new model may be included in a collection
      this.collections = {};
    }
    return cache[cacheKey];
  }

  /**
   * Fetch a model from a resource
   * @param {object} options - Bundle of `id`, `getParams` and `force` for the request
   * @param {string} options.id - id of the model to fetch
   * @param {object} [options.getParams={}] - Query parameters for the request
   * @param {boolean} [options.force=false] - whether to respect the cache when fetching
   * @returns {Promise} Promise that resolves on fetch with the model data
   * @throws {TypeError} - When `id` is missing
   */
  fetchModel({ id, getParams = {}, force = false } = {}) {
    if (!id) {
      throw TypeError('An id must be specified');
    }
    return this.getModel(id, getParams).fetch(force);
  }

  /**
   * Save a model to a resource
   * @param {object} options - Bundle of `id`, `getParams`, `data` and `exists` for the request
   * @param {string} [options.id] - id of the model to save
   * @param {object} [options.getParams={}] - Query parameters for the request
   * @param {object} [options.data] - data to save on the model
   * @param {boolean} [options.exists=false] - Whether the model exists on the server
   * @returns {Promise} Promise that resolves on save with the model data
   */
  saveModel({ id, getParams = {}, data = {}, exists = false } = {}) {
    if (!id) {
      return this.createModel(data, getParams).save();
    }
    return this.getModel(id, getParams).save(data, exists);
  }

  /**
   * Delete a model from a resource
   * @param {object} options - Bundle of `id` and `getParams` identifying the target model
   * @param {string} options.id - id of the model to delete
   * @param {object} [options.getParams={}] - Query parameters for the request
   * @returns {Promise} Promise that resolves on delete with the model id
   * @throws {TypeError} - When `id` is missing
   */
  deleteModel({ id, getParams = {} } = {}) {
    if (!id) {
      throw TypeError('An id must be specified');
    }
    return this.getModel(id, getParams).delete();
  }

  /**
   * Fetch a collection from a resource
   * @param {object} [options] - Bundle of `getParams` and `force` for the request
   * @param {object} [options.getParams={}] - Query parameters for the request
   * @param {boolean} [options.force=false] - whether to respect the cache when fetching
   * @returns {Promise} Promise that resolves on fetch with the collection
   */
  fetchCollection({ getParams = {}, force = false } = {}) {
    return this.getCollection(getParams).fetch(force);
  }

  /**
   * Do a bulk save of a collection, only works for specific resources
   * @param {object} [options] - Bundle of `data` and `getParams` for the bulk save
   * @param {object[]} [options.data] - An array of objects representing the models to be
   * saved.
   * @param {object} [options.getParams] - any getParams to use when caching the collection
   * @returns {Promise} Promise that resolves on save with array of models
   */
  saveCollection({ data = [], getParams = {} } = {}) {
    return this.getCollection(getParams).save(data);
  }

  /**
   * Do a bulk delete of a collection, only works for specific resources, and must use getParams
   * @param {object} getParams - getParams that more narrowly specify the collection to be deleted.
   * @returns {Promise} Promise that resolves on deletion
   */
  deleteCollection(getParams = {}) {
    return this.getCollection(getParams).delete();
  }

  /**
   * Fetch from a custom detail endpoint on a resource, that returns a single JSON object
   * (as opposed to an array of objects).
   * Mostly used as a convenience method for defining additional endpoint fetch methods on a
   * resource object.
   * @param {string} detailName - The name given to the detail endpoint
   * @param {string} id - The id of the model for which this is a detail
   * @param {object} [getParams] - Any getParams needed while fetching
   * @returns {Promise} Promise that resolves on fetch with a single object
   * @throws {TypeError} - When `id` or `detailName` is missing
   */
  fetchDetailModel(detailName, id, getParams = {}) {
    if (!id) {
      throw TypeError('An id must be specified');
    }
    if (!detailName) {
      throw TypeError('A detailName must be specified');
    }
    return this.getModel(id, getParams, detailName).fetch();
  }

  /**
   * Fetch from a custom detail endpoint on a resource, that returns an array of JSON objects
   * (as opposed to a single object).
   * Mostly used as a convenience method for defining additional endpoint fetch methods on a
   * resource object.
   * @param {string} detailName - The name given to the detail endpoint
   * @param {string} id - The id of the model for which this is a detail
   * @param {object} [getParams] - Any getParams needed while fetching
   * @param {boolean} [force] - Whether to bypass the cache
   * @returns {Promise} Promise that resolves on fetch with an array of objects
   * @throws {TypeError} - When `id` or `detailName` is missing
   */
  fetchDetailCollection(detailName, id, getParams = {}, force = false) {
    if (!id) {
      throw TypeError('An id must be specified');
    }
    if (!detailName) {
      throw TypeError('A detailName must be specified');
    }
    return this.getCollection(getParams, detailName, id).fetch(force);
  }

  /**
   * Fetch from a custom list endpoint on a resource, that returns an array of JSON objects.
   * Mostly used as a convenience method for defining additional endpoint fetch methods
   * on a resource object.
   * @param {string} listName - The name given to the list endpoint
   * @param {object} [getParams] - Any getParams needed while fetching
   * @returns {Promise} Promise that resolves on fetch with an array of objects
   * @throws {TypeError} - When `listName` is missing
   */
  fetchListCollection(listName, getParams = {}) {
    if (!listName) {
      throw TypeError('A listName must be specified');
    }
    return this.getCollection(getParams, listName).fetch();
  }

  /**
   * This method is a convenience method for access to a resource endpoint unmediated by the
   * model/collection framework that facilitates caching. This method is for list endpoints.
   * @param {string} method - A valid HTTP method name, in all caps.
   * @param {string} listName - The name given to the list endpoint
   * @param {object} [args] - The getParams or data to be passed to the endpoint,
   * depending on method
   * @param {boolean} [multipart] - Whether to encode the payload as multipart form data
   * @returns {Promise} Promise that resolves with the request
   * @throws {TypeError} - When `listName` is missing
   */
  accessListEndpoint(method, listName, args = {}, multipart = false) {
    if (!listName) {
      throw TypeError('A listName must be specified');
    }
    let data, params;
    if (method.toLowerCase() === 'get') {
      params = args;
    } else {
      data = args;
    }
    return this.client({
      url: this.getUrlFunction(listName)(),
      method,
      data,
      params,
      multipart,
    });
  }

  /**
   * This method is a convenience method for access to a resource endpoint unmediated by the
   * model/collection framework that facilitates caching. This method is for detail endpoints.
   * @param {string} method - A valid HTTP method name, in all caps.
   * @param {string} detailName - The name given to the detail endpoint
   * @param {string} id - The primary key or id for the detail endpoint.
   * @param {object} [args] - The getParams or data to be passed to the endpoint,
   * depending on method
   * @param {boolean} [multipart] - Whether to encode the payload as multipart form data
   * @returns {Promise} Promise that resolves with the request
   * @throws {TypeError} - When `detailName` or `id` is missing
   */
  accessDetailEndpoint(method, detailName, id, args = {}, multipart = false) {
    if (!detailName) {
      throw TypeError('A detailName must be specified');
    }
    if (!id) {
      throw TypeError('An id must be specificied');
    }
    let data, params;
    if (method.toLowerCase() === 'get') {
      params = args;
    } else {
      data = args;
    }
    return this.client({
      url: this.getUrlFunction(detailName)(id),
      method,
      data,
      params,
      multipart,
    });
  }

  get(id) {
    return this.accessDetailEndpoint('get', 'detail', id).then(response => response.data);
  }

  /**
   * Resolve the URL for a named action, applying any route parameters.
   * @param {string} action - The name of the endpoint, as registered with the URL resolver
   * @param {object | Array | string | number} [routeParams] - An object is passed as named
   * kwargs, an array is spread as positional arguments, and any other value is passed as a
   * single positional argument. Omit for a route that takes no parameters.
   * @returns {string} - The resolved URL
   * @throws {ReferenceError} - When no URL is registered for this action
   */
  __resolveUrl(action, routeParams) {
    const urlFunction = this.getUrlFunction(action);
    if (!urlFunction) {
      throw ReferenceError(`No URL found for the ${action} action of the ${this.name} resource`);
    }
    if (routeParams === undefined || routeParams === null) {
      return urlFunction();
    }
    if (Array.isArray(routeParams)) {
      return urlFunction(...routeParams);
    }
    return urlFunction(routeParams);
  }

  /**
   * The single low-level primitive that every read, write, and custom action goes through.
   *
   * Concurrent identical GET requests share a single in-flight request. Writes are never
   * de-duplicated.
   * @param {object} [options] - The request definition
   * @param {string} [options.method=GET] - A valid HTTP method name
   * @param {string} [options.action=list] - The name of the endpoint to target
   * @param {object | Array | string | number} [options.routeParams] - Parameters for the URL
   * itself
   * @param {object} [options.params] - Query parameters
   * @param {object | Array} [options.data] - The request body, for writes
   * @param {boolean} [options.multipart=false] - Whether to encode the body as multipart form
   * data
   * @returns {Promise} - Promise that resolves with the full response object. A caller that
   * coalesces onto an in-flight GET receives a deep copy of `data` unique to it.
   */
  async request({
    method = 'GET',
    action = 'list',
    routeParams,
    params,
    data,
    multipart = false,
  } = {}) {
    const url = this.__resolveUrl(action, routeParams);
    // Log any request failure before re-raising it, so it surfaces with request context the way
    // the legacy Model/Collection layer did. This only intercepts to log - the rejection still
    // propagates to the caller unchanged.
    const logAndRethrow = error => {
      this.logError(error);
      throw error;
    };
    if (method.toUpperCase() !== 'GET') {
      // General case (writes): no dedup, as two writes are two distinct intents.
      return this.client({ url, method, params, data, multipart }).catch(logAndRethrow);
    }
    const key = `${url}?${stableSerialize(params)}`;
    const existing = this._inFlight.get(key);
    if (existing) {
      // Attached to an in-flight request another caller started - clone `data` so the two
      // callers don't share one mutable payload.
      return existing.then(response => ({ ...response, data: cloneDeep(response.data) }));
    }
    // Clear the entry once the request settles, whether it succeeded or failed - a lingering
    // rejected promise would poison every subsequent request for this key. `logError` runs on the
    // shared promise, so coalesced callers do not each re-log the same failure.
    const promise = this.client({ url, method, params })
      .catch(logAndRethrow)
      .finally(() => {
        this._inFlight.delete(key);
      });
    this._inFlight.set(key, promise);
    // The originating caller's payload is unshared by construction, so it needs no copy.
    return promise;
  }

  /**
   * Fetch a single object by id from the resource's default detail endpoint.
   * @param {string} id - The id of the object to fetch
   * @param {object} [options] - Additional request options
   * @param {object} [options.params] - Query parameters
   * @returns {Promise} - Promise that resolves with the object
   * @throws {TypeError} - When `id` is missing
   */
  async retrieve(id, { params } = {}) {
    if (!id) {
      throw TypeError('An id must be specified');
    }
    const response = await this.request({ action: 'detail', routeParams: id, params });
    return response.data;
  }

  /**
   * Fetch a collection of objects from the resource's default list endpoint. A custom
   * list-shaped endpoint (e.g. a detail route that returns a collection) is authored as its
   * own method over `request`, not through here.
   * @param {object} [params] - Query parameters
   * @returns {Promise} - Promise that resolves with the server's response data - either an
   * array of objects, or a `{ results, more, count }` object when the endpoint is paginated
   */
  async list(params = {}) {
    const response = await this.request({ params });
    return response.data;
  }

  /**
   * Create a single object against the resource's default list endpoint.
   * @param {object} data - The object to create
   * @param {boolean} [multipart=false] - Whether to encode the body as multipart form data
   * @returns {Promise} - Promise that resolves with the created object
   */
  async create(data, multipart = false) {
    const response = await this.request({ method: 'POST', data, multipart });
    return response.data;
  }

  /**
   * Update an object. When a `baseline` snapshot is provided, only the fields that differ from
   * it are sent, and an unchanged object issues no request at all.
   * @param {string} id - The id of the object to update
   * @param {object} data - The fields to update
   * @param {object} [options] - Additional request options
   * @param {object} [options.params] - Query parameters
   * @param {object} [options.baseline] - The last server snapshot of this object. When present,
   * `data` is diffed against it and only changed fields are sent; when omitted (or falsy),
   * `data` is sent unchanged.
   * @returns {Promise} - Promise that resolves with the updated object
   * @throws {TypeError} - When `id` is missing
   */
  async update(id, data = {}, { params, baseline } = {}) {
    if (!id) {
      throw TypeError('An id must be specified');
    }
    let payload = data;
    if (baseline) {
      payload = {};
      for (const key of Object.keys(data)) {
        if (!isEqual(data[key], baseline[key])) {
          payload[key] = data[key];
        }
      }
      if (!Object.keys(payload).length) {
        // Nothing changed, so there is nothing to send.
        return cloneDeep(baseline);
      }
    }
    const response = await this.request({
      method: 'PATCH',
      action: 'detail',
      routeParams: id,
      params,
      data: payload,
    });
    return response.data;
  }

  /**
   * Delete a single object by id.
   * @param {string} id - The id of the object to delete
   * @param {object} [options] - Additional request options
   * @param {object} [options.params] - Query parameters
   * @returns {Promise} - Promise that resolves with the id of the deleted object
   * @throws {TypeError} - When `id` is missing
   */
  async delete(id, { params } = {}) {
    if (!id) {
      throw TypeError('An id must be specified');
    }
    await this.request({ method: 'DELETE', action: 'detail', routeParams: id, params });
    return id;
  }

  /**
   * Create several objects in a single request against the resource's default list endpoint.
   * Only works for resources whose endpoint accepts an array payload.
   * @param {object[]} data - The objects to create
   * @param {boolean} [multipart=false] - Whether to encode the body as multipart form data
   * @returns {Promise} - Promise that resolves with the created objects
   * @throws {TypeError} - When `data` is not an array
   */
  async bulkCreate(data, multipart = false) {
    if (!Array.isArray(data)) {
      throw TypeError('An array of objects must be specified');
    }
    const response = await this.request({ method: 'POST', data, multipart });
    return response.data;
  }

  /**
   * Delete every object matching the given query parameters, against the resource's default
   * list endpoint.
   * @param {object} params - Query parameters narrowing what will be deleted
   * @returns {Promise} - Promise that resolves with the server's response data
   * @throws {TypeError} - When no query parameters are given, to prevent an unfiltered
   * bulk delete
   */
  async bulkDelete(params = {}) {
    if (!Object.keys(params).length) {
      throw TypeError('Params must be specified to narrow what is being deleted');
    }
    const response = await this.request({ method: 'DELETE', params });
    return response.data;
  }

  /**
   * @typedef {object} RetrieveObject
   * @property {import('vue').Ref<object|null>} data - The retrieved object, or `null` before the
   * first successful fetch.
   * @property {import('vue').Ref<boolean>} loading - Whether a fetch is currently in flight.
   * @property {import('vue').Ref<?object>} error - The error from the last fetch, or `null`.
   * @property {() => Promise<void>} fetchData - Trigger the fetch. Takes no arguments - reads the
   * bound `id`/`params` at call time.
   */

  /**
   * Reactive read of a single object by id, layered on `useFetch`.
   * Data is not refetched when the params change, caller must call `fetchData` again.
   * @param {string | import('vue').Ref<string> | (() => string)} id - The id of the object to
   * retrieve. A ref or getter is read at fetch time (not watched).
   * @param {object} [options] - Additional options.
   * @param {object | import('vue').Ref<object> | (() => object)} [options.params] - Query
   * parameters passed to `retrieve`. A ref or getter is read at fetch time (not watched).
   * @param {(object: object) => void} [options.onSuccess] - Called with the retrieved object
   * after each successful fetch. When pairing with `useUpdate`, seed both the working data and the
   * baseline here - `onSuccess: obj => { form.value = { ...obj }; setBaseline(obj); }` - so they
   * start in sync; setting the baseline alone leaves `isDirty` true until the working data is
   * populated.
   * @returns {RetrieveObject} The single-object fetch state and action.
   */
  useRetrieve(id, { params, onSuccess } = {}) {
    const { data, loading, error, fetchData } = useFetch({
      fetchMethod: () => this.retrieve(toValue(id), { params: toValue(params) }),
      onSuccess,
    });
    return { data, loading, error, fetchData };
  }

  /**
   * Reactive read of a collection, layered on `useFetch`.
   * Data is not refetched when the params change, caller must call `fetchData` again.
   *
   * Pagination is driven by the endpoint's shape, and the two backend styles map to two usage
   * modes:
   *  - cursor / limit-offset endpoints emit a `more` cursor: append the next page with
   *    `fetchMore()`, gated by `hasMore` (infinite scroll).
   *  - page-number endpoints emit `page`/`total_pages`: show one page at a time by updating the
   *    page in `params` and re-calling `fetchData()`, reading `page`/`totalPages` for the pager.
   *    `hasMore`/`fetchMore` do not apply to these (`hasMore` stays `false`).
   * @param {object | import('vue').Ref<object> | (() => object)} [params] - Query parameters
   * passed to `list`. A ref or getter is read at fetch time (not watched).
   * @param {object} [options] - Additional options.
   * @param {(response: object) => void} [options.onSuccess] - Called with the raw list response
   * after each successful `fetchData` - an array, or a `{ results, more, count }` /
   * `{ results, page, total_pages, count }` object when paginated. Not called for `fetchMore`,
   * so a consumer mirroring results into a cache must handle appended pages separately.
   * @returns {import('kolibri/composables/useFetch').FetchObject} The fetch state and actions.
   * The returned `fetchData` takes no arguments.
   */
  useList(params, { onSuccess } = {}) {
    return useFetch({
      fetchMethod: () => this.list(toValue(params)),
      onSuccess,
      // `more` is the complete set of query parameters for the next page, so it replaces
      // rather than extends the original params.
      fetchMoreMethod: more => this.list(more),
    });
  }

  /**
   * @typedef {object} UpdateObject
   * @property {import('vue').Ref<boolean>} isSaving - Whether a write is in flight. Stays true
   * until the latest-issued write settles. The latest-issued write only wins the local
   * bookkeeping (baseline / `isSaving` / `error`); writes are not serialised on the wire, so a
   * rapidly re-issued save can still race server-side.
   * @property {import('vue').Ref<Error|null>} error - The error from the last write, or `null`. On
   * failure `create()`/`update()` set this and also reject.
   * @property {import('vue').ComputedRef<boolean>} isDirty - Whether the working `data` differs
   * from the last saved snapshot - the unsaved-changes signal an editor needs. Only meaningful
   * when `data` has the same shape as the server object; a `data` that is a projection of a larger
   * object reads dirty against the fuller baseline. On load, seed the working data alongside the
   * baseline (see `useRetrieve`'s `onSuccess`), or this reads true before any edit.
   * @property {(object: object|null) => void} setBaseline - Adopt a server snapshot as the
   * baseline `update` diffs against (deep-copied); pass `null` to forget it. Typically wired to
   * `useRetrieve`'s `onSuccess` to record the baseline on load.
   * @property {() => Promise<object>} create - POST the bound `data` and record the created object
   * as the next baseline. Rejects if the bound `id` is already set.
   * @property {() => Promise<object>} update - PATCH only the fields of `data` that differ from
   * the baseline. Rejects if the bound `id` is empty.
   * @property {() => Promise<object>} save - Dispatch on the bound `id`: `create()` when empty,
   * `update()` otherwise, so call sites need not branch.
   */

  /**
   * A write helper bound to a single object of this resource, scoped to the calling component.
   * Rather than taking the object per call, the identity (`id`) and the working payload (`data`)
   * are bound once here, so this composable is bound to a single object at a time, never arbitrary
   * items. After `create()` resolves, the caller can set the new `id` to keep editing the same
   * object.
   *
   *  - when `id` is empty, `create()` is valid and `update()` rejects;
   *  - when `id` is set, `update()` is valid and `create()` rejects.
   *
   * `create()`, `update()`, and `save()` take no arguments - they read the bound refs at call
   * time.
   * @param {string | import('vue').Ref<string> | (() => string)} id - The object's id, or empty
   * for a not-yet-created object. A ref or getter is read at call time (not watched).
   * @param {object | import('vue').Ref<object> | (() => object)} data - The working payload to
   * send. A ref or getter is read at call time (not watched).
   * @param {object} [options] - Additional options.
   * @param {object | import('vue').Ref<object> | (() => object)} [options.params] - Query
   * parameters for `update`.
   * @param {boolean} [options.multipart] - Whether `create` should send multipart form data.
   * @returns {UpdateObject} The write state and actions.
   */
  useUpdate(id, data, { params, multipart } = {}) {
    const isSaving = ref(false);
    const error = ref(null);
    // The last server snapshot. Written only through setBaseline / create / update; reactive so
    // `isDirty` can derive from it (shallow is enough - it is replaced wholesale, never mutated).
    const baseline = shallowRef(null);

    // Store a full copy, so later mutation of the caller's object cannot corrupt the snapshot
    // we diff against.
    const setBaseline = object => {
      baseline.value = object == null ? null : cloneDeep(object);
    };

    // True when the working `data` differs from the last saved snapshot. Only meaningful when
    // `data` has the same shape as the server object; a `data` that is a projection of a larger
    // object (e.g. a subset of fields) will read dirty against the fuller baseline.
    const isDirty = computed(
      () => baseline.value != null && !isEqual(toValue(data), baseline.value),
    );

    // A monotonic token, captured per call and re-checked before touching shared state, so a
    // superseded write's out-of-order response cannot record a stale baseline (which would make
    // the next diff short-circuit to a silent no-op) or flip isSaving/error for a call still in
    // flight.
    let writeCount = 0;
    const run = async request => {
      const current = ++writeCount;
      isSaving.value = true;
      error.value = null;
      try {
        const saved = await request();
        // The saved object is the freshest server truth, so it becomes the next baseline.
        if (current === writeCount) {
          setBaseline(saved);
        }
        return saved;
      } catch (e) {
        if (current === writeCount) {
          error.value = e;
        }
        throw e;
      } finally {
        if (current === writeCount) {
          isSaving.value = false;
        }
      }
    };

    const create = () => {
      if (toValue(id)) {
        return Promise.reject(
          TypeError('create() called for an object that already has an id; use update()'),
        );
      }
      return run(() => this.create(toValue(data), multipart));
    };

    const update = () => {
      const currentId = toValue(id);
      if (!currentId) {
        return Promise.reject(TypeError('update() called without an id; use create()'));
      }
      // Only diff against the baseline when it belongs to the object currently bound. If `id`
      // was rebound before a fresh baseline was installed, a stale baseline from the previous
      // object could silently drop fields whose edited value coincides with the old one; a
      // mismatched baseline degrades to a full PATCH, which is always correct.
      const currentBaseline =
        baseline.value != null && String(baseline.value[this.idKey]) === String(currentId)
          ? baseline.value
          : undefined;
      return run(() =>
        this.update(currentId, toValue(data), {
          params: toValue(params),
          baseline: currentBaseline,
        }),
      );
    };

    // Dispatch on the bound id so call sites need not branch: create a not-yet-persisted
    // object, otherwise update the existing one.
    const save = () => (toValue(id) ? update() : create());

    return { isSaving, error, isDirty, setBaseline, create, update, save };
  }

  /**
   * Call a GET on a custom list endpoint
   * @param {string} listName - The name given to the list endpoint
   * @param {object} params - The getParams to be passed to the endpoint
   * @returns {Promise} Promise that resolves with the request
   */
  getListEndpoint(listName, params = {}) {
    return this.accessListEndpoint('get', listName, params);
  }

  /**
   * Call a POST on a custom list endpoint
   * @param {string} listName - The name given to the list endpoint
   * @param {object} params - The body of the request
   * @returns {Promise} Promise that resolves with the request
   */
  postListEndpoint(listName, params = {}) {
    return this.accessListEndpoint('post', listName, params);
  }

  /**
   * Call a POST on a custom list endpoint and use
   * 'multipart/form-data' as Mimetype instead of 'application/json'.
   * @param {string} listName - The name given to the list endpoint
   * @param {object} params - The body of the request
   * @returns {Promise} Promise that resolves with the request
   */
  postListEndpointMultipart(listName, params = {}) {
    return this.accessListEndpoint('post', listName, params, true);
  }

  /**
   * Call a POST on a custom detail endpoint
   * @param {string} detailName - The name given to the detail endpoint
   * @param {string} id - The id for the detail endpoint
   * @param {object} params - The body of the request
   * @returns {Promise} Promise that resolves with the request
   */
  postDetailEndpoint(detailName, id, params = {}) {
    return this.accessDetailEndpoint('post', detailName, id, params);
  }

  /**
   * Reset the cache for this Resource.
   */
  clearCache() {
    this.models = {};
    this.collections = {};
  }

  unCacheModel(id, getParams = {}, endpointName) {
    const cacheKey = this.__cacheKey({ [this.idKey]: id }, getParams);
    if (this.__modelCache(endpointName)[cacheKey]) {
      this.__modelCache(endpointName)[cacheKey].synced = false;
    }
  }

  unCacheCollection(getParams = {}, endpointName) {
    const cacheKey = this.__cacheKey(getParams);
    if (this.__collectionCache(endpointName)[cacheKey]) {
      this.__collectionCache(endpointName)[cacheKey].synced = false;
    }
  }

  removeModel(model, endpointName) {
    const cacheKey = this.__cacheKey({ [this.idKey]: model.id }, model.getParams);
    delete this.__modelCache(endpointName)[cacheKey];
  }

  removeCollection(collection, endpointName) {
    const cacheKey = this.__cacheKey(collection.getParams);
    delete this.__collectionCache(endpointName)[cacheKey];
  }

  get urls() {
    return urls;
  }

  getUrlFunction(endpoint) {
    return this.urls[`${this.name}_${endpoint}`];
  }

  get modelUrl() {
    // Leveraging Django REST Framework generated URL patterns.
    return this.getUrlFunction('detail');
  }

  get collectionUrl() {
    // Leveraging Django REST Framework generated URL patterns.
    return this.getUrlFunction('list');
  }

  client(options) {
    // eslint-disable-next-line import-x/no-commonjs
    const client = require('kolibri/client').default;
    return client(options);
  }

  logError(err) {
    // eslint-disable-next-line import-x/no-commonjs
    const router = require('kolibri/router').default;
    // Only log what we can describe. A network failure (or a cancellation) has no `response`, so
    // reading `err.response.statusText` below would throw and mask the real error - bail early.
    if (!err.config || !err.response) {
      return;
    }
    const sanitized = sanitizeError(err);
    /* eslint-disable no-console */
    console.groupCollapsed(
      `%cRequest error: ${err.response.statusText}, ${
        err.response.status
      } for ${err.config.method.toUpperCase()} to ${err.config.url} - open for more info`,
      'color: red',
    );
    console.log(`Error occured for ${this.name} resource on page ${window.location.href}`);
    const currentRoute = router.currentRoute;
    if (currentRoute) {
      console.group('Vue Router');
      console.log(`fullPath: ${currentRoute.fullPath}`);
      console.log(`Route name: ${currentRoute.name}`);
      if (Object.keys(currentRoute.params).length) {
        console.group('Vue router params');
        for (const [k, v] of Object.entries(currentRoute.params)) {
          console.log(`${k}: ${v}`);
        }
        console.groupEnd();
      }
      console.groupEnd();
    }
    if (sanitized.config?.params && Object.keys(sanitized.config.params).length) {
      console.group('Query parameters');
      for (const [k, v] of Object.entries(sanitized.config.params)) {
        console.log(`${k}: ${v}`);
      }
      console.groupEnd();
    }
    if (sanitized.config?.data) {
      try {
        const data = JSON.parse(sanitized.config.data);
        if (Object.keys(data).length) {
          console.group('Data');
          for (const [k, v] of Object.entries(data)) {
            console.log(`${k}: ${v}`);
          }
          console.groupEnd();
        }
      } catch (e) {} // eslint-disable-line no-empty
    }
    if (sanitized.config?.headers && Object.keys(sanitized.config.headers).length) {
      console.group('Headers');
      for (const [k, v] of Object.entries(sanitized.config.headers)) {
        console.log(`${k}: ${v}`);
      }
      console.groupEnd();
    }
    console.trace('Traceback for request');
    console.groupEnd();
    /* eslint-enable */
  }
}
